/**
 * Cynaps Open Source - State Registry
 *
 * This file registers core Cynaps project states with the state registry from app-common.
 *
 * The base registry (imported from @cynaps/app-common) provides:
 * - StateRegistry class and singleton instance
 * - StateType enum (INITIAL, IN_PROGRESS, ATTENTION, TERMINAL)
 * - Helper functions (getStateColorClass, formatStateName, etc.)
 *
 * Cynaps supports a simplified state model with 3 core project states:
 * - CREATED (initial state)
 * - ANNOTATION_IN_PROGRESS (work in progress)
 * - COMPLETED (terminal state)
 *
 * IMPORTANT: Import this file in your main.tsx or app initialization:
 * ```typescript
 * import './utils/state-registry';
 * ```
 */

import { stateRegistry, StateType } from "@cynaps/app-common";

// ============================================================================
// Project States (Cynaps Core)
// ============================================================================

/**
 * Cynaps has a simplified state model with 3 core states.
 */
stateRegistry.registerBatch({
  CREATED: {
    type: StateType.INITIAL,
    label: "Created",
    tooltips: {
      project: "Project has been created and is ready for configuration",
    },
  },

  ANNOTATION_IN_PROGRESS: {
    type: StateType.IN_PROGRESS,
    label: "In Progress",
    tooltips: {
      project: "Annotation work is in progress on this project",
      task: "Task is being annotated",
    },
  },

  COMPLETED: {
    type: StateType.TERMINAL,
    label: "Completed",
    tooltips: {
      project: "All work on this project is completed",
      task: "Task has been completed",
    },
  },
});

// ============================================================================
// Development Validation
// ============================================================================

/**
 * In development mode, verify all expected Cynaps states are properly registered.
 * This helps catch configuration issues early.
 */
if (process.env.NODE_ENV === "development") {
  const cynapsStates = ["CREATED", "ANNOTATION_IN_PROGRESS", "COMPLETED"];

  const missingStates = cynapsStates.filter((state) => !stateRegistry.isRegistered(state));

  if (missingStates.length > 0) {
    console.error("[Cynaps State Registry] Missing state registrations:", missingStates);
  } else {
    console.log("[Cynaps State Registry] ✅ All Cynaps states registered successfully");
    console.log(`[Cynaps State Registry] Registered ${cynapsStates.length} Cynaps states`);
  }

  // Log all registered states for debugging
  if (process.env.DEBUG_STATE_REGISTRY) {
    console.log("[Cynaps State Registry] All registered states:", stateRegistry.getAllStates());
  }
}
